import { forkJoin, from, interval, Observable, of, timer, zip } from 'rxjs'
import { API_ENDPOINTS, ConditionReportPDFGenerator, Wemi, WemiUtils } from '@skinsoft/entity'
import { set } from 'lodash'
import { catchError, concatMap, every, first, map, mergeMap, retryWhen, switchMap, tap } from 'rxjs/operators'
import { ThesaurusTerm } from '../thesaurusTerm'

import { EntityDataStore } from '@skinsoft/entity-data-store'
import { TranslatedString } from '@skinsoft/i18n'
import { createTranslatedString, getCurrentDateAsSkinDate, SkinDate } from '@skinsoft/ui'
import _ from 'lodash'
import { MEDIA_SUBJECTS_KEYS, PushCachedValueResult, SynchroMediaUpdates } from '@skinsoft/data-store'
import { AssociatedMedias, ConditionReportPDFTransferHelper, ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { involvedItem, referenceNumber, workflowState } from './selectors'

export interface ConditionReport {
    id: string
    associatedMedias: any
    conditionReportCS: {
        alterations: Alteration[],
        alterationDetails: DefectElement[] | null,
        referenceNumber: string,
        comments: any[],
        reportDate: SkinDate,
        signers?: Signer[],
        stateOfConservations?: StateOfConservation[],
        predictInterventions?: PredictedIntervention[],
        reportReason?: string,
    }
    workflowSchema?: any
}

export interface Signer {
    id: string
    signer: TranslatedString
    signature: string
}

export interface StateOfConservation {
    stateOfConservation: string | null,
    precision: TranslatedString | null
}

export interface PredictedIntervention {
    typeIntervention: string | null,
    precision: TranslatedString | null
}

export interface Alteration {
    media: string | null,
    drawing: string,
    title: TranslatedString,
    precision: TranslatedString,
    typeOfAlteration: string, //ThesaurusTerm id
    detailedViews: string[],
}

export interface DefectAnnotation {
    id: number
    title: TranslatedString
    comments: TranslatedString[]
    media: string
    svg: string
}

export interface DefectAlteration {
    id: number
    typeOfAlteration: ThesaurusTerm['id']
    precision: TranslatedString
    annotations: DefectAnnotation[]
}

export interface DefectMaterial {
    id: number
    material: ThesaurusTerm['id']
    alterations: DefectAlteration[]
}

export interface DefectObservation {
    id: number
    typeOfObservation: ThesaurusTerm['id']
    materialAlterations: DefectMaterial[]
}

export interface DefectElement {
    id: number
    observationAlterationDetails: DefectObservation[]
    element: TranslatedString
    precision: TranslatedString
}

interface AdditionalSynchroData {
    alterationsMedia?: { media?: string }[],
}

export enum ConditionReportState {
    IN_PROGRESS = 'ui.lifecycle-state.inProgress',
    CLOSED = 'ui.lifecycle-state.validated',
}

// Methods to create empty alterations tree parts
export const getMinimumDefectAlterations = (locale: string) => {
    return [
        {
            id: 0,
            // tslint:disable-next-line:object-literal-sort-keys
            element: createTranslatedString(locale),
            precision: createTranslatedString(locale),
            // tslint:disable-next-line:object-literal-sort-keys
            observationAlterationDetails: getMinimumObservations(locale),
        },
    ] as DefectElement[]
}

export const getMinimumObservations = (locale: string) => {
    return [
        {
            id: 0,
            typeOfObservation: '',
            // tslint:disable-next-line:object-literal-sort-keys
            materialAlterations: getMinimumMaterials(locale),
        },
    ] as DefectObservation[]
}

export const getMinimumMaterials = (locale: string) => {
    return [
        {
            id: 0,
            material: '',
            // tslint:disable-next-line:object-literal-sort-keys
            alterations: getMinimumAlterations(locale),
        },
    ] as DefectMaterial[]
}

export const getMinimumAlterations = (locale: string) => {
    return [
        {
            id: 0,
            typeOfAlteration: '',
            // tslint:disable-next-line:object-literal-sort-keys
            precision: createTranslatedString(locale),
            annotations: [],
        },
    ] as DefectAlteration[]
}

export class ConditionReportDataStore extends EntityDataStore<ConditionReport> {
    public static getInstance() {
        if (!ConditionReportDataStore.instance) {
            ConditionReportDataStore.instance = new ConditionReportDataStore()
        }

        return ConditionReportDataStore.instance
    }

    public static install(Vue) {
        Vue.prototype.$conditionReportDataStore = ConditionReportDataStore.getInstance()
    }

    private static instance: ConditionReportDataStore = null as any

    constructor() {
        super('conditionReports', ['conditionReport'])
    }

    public exists(id: ConditionReport['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise()
    }

    public fetchOneOnlineMode(id: string): Observable<ConditionReport> {
        return super.fetchOneOnlineMode(id).pipe(
            switchMap(conditionReport => {
                if(!conditionReport.associatedMedias) {
                    _.set(conditionReport, 'associatedMedias', {
                        medias: [],
                        first: undefined,
                        second: undefined,
                        third: undefined
                    })
                }

                return of(conditionReport)
            })
        )
    } 

    public async closeConditionReport(reportId: string, mediaId: string) {
        let reportUpdates: Partial<ConditionReport> = {
            workflowSchema: {
                currentState: {
                    name: 'validated'
                }
            }
        }
        
        const hasCachedUpdates = await this.hasCachedUpdates(reportId)
        if(window.navigator.onLine && ! hasCachedUpdates && ! MediaTransferHelper.isLocalMedia(mediaId)) {
            this.fetchOne(reportId, true).pipe(
                switchMap(report => {
                    let associatedMedias = report.associatedMedias
                    associatedMedias.medias = associatedMedias.medias ? associatedMedias.medias.concat([mediaId]) : [mediaId]
                    associatedMedias.medias = _.uniq(associatedMedias.medias)
                    reportUpdates.associatedMedias = MediaTransferHelper.permutateFirstMedia(mediaId, associatedMedias)
                    const body = this.mergeOverridingArrays(report, reportUpdates)

                    return this.update(reportId, body)
                }),
                concatMap(() => this.fetchOne(reportId, true)),
                first(),
            )
            .toPromise()
        }
        else {
            return this.fetchOneOfflineMode(reportId).pipe(
                switchMap(report => {
                    reportUpdates.associatedMedias = MediaTransferHelper.permutateFirstMedia(mediaId, report.associatedMedias)
                    return this.updateOfflineMode(reportId, reportUpdates)
                })
            )
            .toPromise()
        }
    }

    public async closeConditionReportOffline(reportId: string) {
        let reportUpdates: Partial<ConditionReport> = {
            workflowSchema: {
                currentState: {
                    name: 'validated'
                }
            }
        }

        return this.fetchOneOfflineMode(reportId).pipe(
            switchMap(report => {
                return this.updateOfflineMode(reportId, reportUpdates)
            })
        )
        .toPromise()
    }

    private handleReplaySubjects(pushResult: PushCachedValueResult, dataToPush: SynchroMediaUpdates<ConditionReport>, additionalSynchroData: AdditionalSynchroData): Observable<PushCachedValueResult> {
        if(dataToPush.id) {
            let replaySubjectsResults = [of({ success: true } as PushCachedValueResult)]

            const imageSubject = dataToPush.mediaSubjects[MEDIA_SUBJECTS_KEYS.image]
    
            const timer = interval(1000)

            let newFirstMediaId: string | null = null
    
            if(imageSubject) {
                // combination zip + timer = time output from another source
                replaySubjectsResults.push(zip(imageSubject, timer).pipe(map(([a, _]) => a)).pipe(
                    mergeMap((replaySubjectContent): Observable<PushCachedValueResult> => {
                        if (replaySubjectContent.newMediaId) {
                            return this.addMedia(dataToPush.id, replaySubjectContent as { newMediaId: string }).pipe(
                                tap(() => {
                                    ImageTransferHelper.getInstance().deleteImageInCache(replaySubjectContent.newMediaId as string)
                                    if(replaySubjectContent.cacheKeys) {
                                        for(const cacheKey of replaySubjectContent.cacheKeys) {
                                            if(dataToPush.cachedMedias.first && replaySubjectContent.cacheKeys.includes(dataToPush.cachedMedias.first)) {
                                                newFirstMediaId = replaySubjectContent.newMediaId as string
                                            }
                                            ImageTransferHelper.getInstance().deleteImageInCache(cacheKey)

                                            // updating 'alteration copy' localmedias id
                                            additionalSynchroData.alterationsMedia?.forEach(alteration => {
                                                if(alteration.media === cacheKey) {
                                                    alteration.media = replaySubjectContent.newMediaId
                                                }
                                            })
                                        }
                                    }
                                }),
                                tap(() => {
                                    ImageTransferHelper.getInstance().addImageToReporterSubAlbum(replaySubjectContent.newMediaId)
                                }),
                                switchMap(() => of({
                                    success: true
                                })),
                            )
                        }

                        return of({
                            success: false
                        })
                    }),
                    every((result: PushCachedValueResult) => result.success),
                    mergeMap(res => {
                        // fetching conditionReport (needed for updating first media)
                        return this.fetchOneOnlineMode(dataToPush.id).pipe(
                            switchMap(conditionReport => of({
                                res,
                                conditionReport
                            }))
                        ) 
                    }),
                    mergeMap(({res, conditionReport}) => {
                        const hasLocalMediaReferenceToUpdate: boolean = (newFirstMediaId != null || additionalSynchroData?.alterationsMedia != null)
                        
                        if(hasLocalMediaReferenceToUpdate) {
                            let body = {...conditionReport}

                            if(newFirstMediaId) {
                                // updating first media after every media has been uploaded to the API (successfully or not) to ensure that the associated 
                                // medias will not change during/after this operation (prevents data from being in an unknown/unstable state)
                                let updatedAssociatedMedias: AssociatedMedias

                                if(workflowState(dataToPush.body) === 'validated') {
                                    const pdfId = body.associatedMedias.first
                                    body.associatedMedias.first = null
                                    updatedAssociatedMedias = MediaTransferHelper.permutateFirstMedia(newFirstMediaId, conditionReport.associatedMedias)
                                    updatedAssociatedMedias = MediaTransferHelper.permutateFirstMedia(pdfId, updatedAssociatedMedias)
                                } else {
                                    updatedAssociatedMedias = MediaTransferHelper.permutateFirstMedia(newFirstMediaId, conditionReport.associatedMedias)
                                }
                                
                                _.set(body, 'associatedMedias', updatedAssociatedMedias)
                            }

                            if(additionalSynchroData?.alterationsMedia) {
                                // updating alterations medias
                                if(body.conditionReportCS.alterations && additionalSynchroData.alterationsMedia) {
                                    _.merge(body.conditionReportCS.alterations, additionalSynchroData.alterationsMedia)
                                }
                            }

                            return this.proxy.switchToRequestSuccessObservable(this.update(dataToPush.id, body)).pipe(
                                switchMap(requestRes => of(requestRes && res)),
                            )
                        }

                        return of(res)
                    }),
                    mergeMap(res => of({ success: res })),
                ))
            }

            return forkJoin(replaySubjectsResults).pipe(
                switchMap((results) => {
                    let finalResult = {
                        success: results.every(result => result.success) && pushResult.success,
                    }
                    if(pushResult.newId) {
                        (finalResult as any).newId = pushResult.newId
                    }
                    return of(finalResult)
                })
            )
        }

        return of({ 
            success: false 
        })
    }

    private generateAndUploadPDF(conditionReport: ConditionReport): Observable<string> {
        const itemId = involvedItem(conditionReport)

        return WemiUtils.getWemiFromItem(itemId).pipe(
            switchMap((associatedItem: Wemi) => {
                // We use a promise here in order to wait for the PDFGenerator.generatePDF to complete (using the callback argument) 
                return from(new Promise<File>((resolve, reject) => {
                    if(associatedItem) {
                        ConditionReportPDFGenerator.getInstance().generatePDF(conditionReport, associatedItem, (blob) => {
                            const fileName = referenceNumber(conditionReport) ? `${referenceNumber(conditionReport)}.pdf` : `${MediaTransferHelper.generateMediaHash()}.pdf`
                            resolve(new File([blob], fileName))
                        })
                        .catch(err => {
                            reject()
                        })

                    } else {
                        reject()
                    }
                }))
            }),
            switchMap(file => ConditionReportPDFTransferHelper.getInstance().uploadPDFDocument("application/pdf", conditionReport.id, file, true)),
        )
    }

    private applyAfterPushOperations(pushResult: PushCachedValueResult, dataPushed: SynchroMediaUpdates<ConditionReport>, additionalSynchroData: AdditionalSynchroData): Observable<PushCachedValueResult> {
        let result: Observable<PushCachedValueResult> = of(pushResult) 
        
        if(!pushResult.success) {
            return result
        }

        if(workflowState(dataPushed.body) === 'validated') {
            result = this.fetchOneOfflineMode(dataPushed.id).pipe(
                switchMap((conditionReportFromCache: ConditionReport) => {
                    return this.generateAndUploadPDF(conditionReportFromCache)
                }),
                switchMap((pdfId) => {
                    return zip(of(pdfId), this.fetchOneOnlineMode(dataPushed.id))
                }),
                switchMap(([pdfId, conditionReport]) => {
                    // set pdf as first media
                    const body = {...conditionReport}
                    let associatedMedias = conditionReport.associatedMedias
                    associatedMedias.medias = associatedMedias.medias ? associatedMedias.medias.concat([pdfId]) : [pdfId]
                    associatedMedias.medias = _.uniq(associatedMedias.medias)
                    const sortedMedias = MediaTransferHelper.permutateFirstMedia(pdfId, associatedMedias)
                    _.set(body, 'associatedMedias', sortedMedias)
                    return this.update(dataPushed.id, body)
                }),
                first(),
                // Handle success or error in the update and emit boolean
                catchError(e => of(Error('Error during update...'))),
                switchMap(
                    response => {
                        if (response instanceof Error) {
                            return of({
                                success: false
                            });
                        }
                        return of(pushResult)
                    }
                ),
            )
        }

        return result.pipe(
            switchMap((res: PushCachedValueResult) => {
                if(res && res.success) {
                    return this.handleReplaySubjects(res, dataPushed, additionalSynchroData)
                }

                return of({
                    success: false
                })
            })
        )
    }

    private removeLocalMediaFromAlterations(dataToPush: SynchroMediaUpdates<ConditionReport>, additionalSynchroData: AdditionalSynchroData) {
        additionalSynchroData.alterationsMedia = []
        if(dataToPush.body.conditionReportCS.alterations && Array.isArray(dataToPush.body.conditionReportCS.alterations)) {
            for(let alteration of dataToPush.body.conditionReportCS.alterations) {
                if(alteration.media?.startsWith('local')){
                    additionalSynchroData.alterationsMedia.push({ media: alteration.media })
                    alteration.media = null
                    continue
                }

                additionalSynchroData.alterationsMedia.push({ })
            }
        }
    }

    private applyBeforePushOperations(dataToPush: SynchroMediaUpdates<ConditionReport>): AdditionalSynchroData {
        let additionalSynchroData: AdditionalSynchroData = {}
        this.removeLocalMediaFromAlterations(dataToPush, additionalSynchroData)
        return additionalSynchroData
    }

    protected pushCachedUpdate(dataToUpdate: SynchroMediaUpdates<ConditionReport>): Observable<PushCachedValueResult> {
        const additionalSynchroData = this.applyBeforePushOperations(dataToUpdate)
        return super.pushCachedUpdate(dataToUpdate).pipe(
            switchMap((res: PushCachedValueResult) => this.applyAfterPushOperations(res, dataToUpdate, additionalSynchroData))
        )
    }

    protected pushCachedCreations(dataToCreate): Observable<PushCachedValueResult> {
        const additionalSynchroData = this.applyBeforePushOperations(dataToCreate)
        return super.pushCachedCreations(dataToCreate).pipe(
            switchMap((res: PushCachedValueResult) => {
                let dataToCreateCopy = {...dataToCreate}
                if(res && res.success) {
                    if(res.newId) {
                        dataToCreateCopy.id = res.newId
                        // we update the id in the cache to conserve access to cache data after updating the id in dataToCreate
                        return from(this.offlineDataUpdateCacher.renameCacheKey(dataToCreate.id, res.newId)).pipe(
                            switchMap(() => this.applyAfterPushOperations(res, dataToCreateCopy, additionalSynchroData))
                        )
                    }
                }
                return this.applyAfterPushOperations(res, dataToCreateCopy, additionalSynchroData)
            })
        )
    }

    public addMedia(reportId: string, media: {newMediaId: string}) {
        return this.proxy.fetch(`/conditionReports/addMedia/${reportId}`,{idMedia: media.newMediaId}, 'PUT')
    }

    public duplicate(entityToDuplicateId: string) {
        return super.duplicate(entityToDuplicateId, undefined, {
            conditionReportCS: {
                reportDate: getCurrentDateAsSkinDate(),
            } as any,
        })
    }

    public create(
        createBody?: Partial<ConditionReport>,
        body?: Partial<ConditionReport>,
    ): Observable<ConditionReport> {
        if (window.navigator.onLine) {
            return super.create(createBody, body)
        } else {
            if (!body) {
                return this.createOffline()
            } else {
                return super.create(createBody, body)
            }
        }
    }

    public createOffline(): Observable<ConditionReport> {
        const tempId =
            '_' +
            Math.random()
                .toString(36)
                .substr(2, 9) +
            Math.random()
                .toString(36)
                .substr(2, 9)
        const emptyReport = {
            id: tempId,
            conditionReportCS: {
                signers: [] as Signer[],
                stateOfConservations: [] as StateOfConservation[],
                predictInterventions: [] as PredictedIntervention[],
                alterationDetails: null
            },
            workflowSchema: {
                currentState: {
                    name: 'inProgress',
                    availableLifecycleStates: ['validated']
                }
            },
        }
        this.offlineDataUpdateCacher.storeBeforePushing(tempId, emptyReport as Partial<
            ConditionReport
        > & { id: string })

        return of('_').pipe(
            switchMap(() => this.fetchOneOfflineMode(tempId)),
            tap(conditionReport => {
                if (!conditionReport) {
                    throw new Error('ConditionReport not yet available')
                }
            }),
            retryWhen(errors => errors.pipe(() => timer(1000))),
        )
    }

    public update(
        id: string,
        body: Partial<ConditionReport>,
        endpointOverride?: string,
        ignoreCachedStatus: boolean = false
    ): Observable<ConditionReport> {
        return super.update(id, body, endpointOverride || API_ENDPOINTS['conditionReport'], ignoreCachedStatus)
    }

    public updateOfflineMode(
        id: string,
        body: Partial<ConditionReport>,
    ): Observable<ConditionReport> {
        return super.updateOfflineMode(id, body)
    }

    public async setInvolvedItemAndSave(reportId: string, itemId: string) {
        const report: ConditionReport = await this.fetchOne(reportId)
            .pipe(first())
            .toPromise()

        set(report, 'conditionReportCS.linkableItem.linkableItem', itemId)
    }
}
